import React, { useState, useEffect } from 'react';
import { Bookmark, Save, Trash2, Check } from 'lucide-react';
import { fetchFormTemplates, saveFormTemplate, deleteFormTemplate, FormTemplateData } from '../../lib/specialRequestTemplates';
import { confirmDialog } from '../../lib/confirmStore';
import SearchableSelect from '../shared/SearchableSelect';

interface FormTemplatePickerBarProps {
  onSelectTemplate: (template: FormTemplateData) => void;
  getCurrentFormData: () => Omit<FormTemplateData, 'templateName'>;
}

export default function FormTemplatePickerBar({
  onSelectTemplate,
  getCurrentFormData,
}: FormTemplatePickerBarProps) {
  const [templates, setTemplates] = useState<FormTemplateData[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);

  const loadTemplates = async () => {
    const list = await fetchFormTemplates();
    setTemplates(list);
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const handleSelect = (id: string) => {
    setSelectedId(id);
    const found = templates.find(t => t.id === id);
    if (found) {
      onSelectTemplate(found);
    }
  };

  const handleSave = async () => {
    if (!templateName.trim()) return;
    setSaving(true);
    try {
      const formData = getCurrentFormData();
      const newTmpl = await saveFormTemplate({
        ...formData,
        templateName: templateName.trim(),
      });
      setTemplateName('');
      setShowSaveInput(false);
      await loadTemplates();
      setSelectedId(newTmpl.id || '');
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!(await confirmDialog('Are you sure you want to delete this template? This cannot be undone.'))) return;
    await deleteFormTemplate(id);
    if (selectedId === id) setSelectedId('');
    await loadTemplates();
  };

  return (
    <div className="p-3 bg-slate-50 border border-slate-200/80 rounded-xl space-y-2 mb-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center space-x-1.5 text-xs font-bold text-slate-700">
          <Bookmark className="h-3.5 w-3.5 text-brand" />
          <span>Form Template Workspace</span>
        </div>

        <div className="flex items-center space-x-2 shrink-0">
          {!showSaveInput ? (
            <button
              type="button"
              onClick={() => setShowSaveInput(true)}
              className="px-2.5 py-1 bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 rounded-lg text-caption font-semibold flex items-center gap-1 transition cursor-pointer"
            >
              <Save className="h-3 w-3 text-brand" />
              <span>Save Form as Template</span>
            </button>
          ) : (
            <div className="flex items-center space-x-1">
              <input
                type="text"
                placeholder="Template name..."
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
                className="px-2 py-1 text-caption border border-slate-300 rounded-lg bg-white outline-none focus:border-brand w-36"
              />
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !templateName.trim()}
                className="px-2 py-1 bg-brand text-white rounded-lg text-caption font-bold hover:bg-brand-dark disabled:opacity-50 cursor-pointer flex items-center gap-1"
              >
                <Check className="h-3 w-3" />
                <span>Save</span>
              </button>
              <button
                type="button"
                onClick={() => setShowSaveInput(false)}
                className="px-2 py-1 text-slate-400 hover:text-slate-600 rounded-lg text-caption"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Select Template dropdown */}
      {templates.length > 0 ? (
        <div className="flex items-center space-x-2 flex-1">
          <div className="flex-1">
            <SearchableSelect
              value={selectedId}
              onChange={(v) => handleSelect(v)}
              options={templates.map(t => ({
                value: t.id,
                label: `${t.templateName} (${t.responseType})`,
              }))}
              placeholder="Search saved templates…"
              clearLabel="-- Load Saved Template --"
              emptyLabel="No saved templates found"
            />
          </div>
          {selectedId && (
            <button
              type="button"
              onClick={(e) => handleDelete(e, selectedId)}
              className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition cursor-pointer"
              title="Delete selected template"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ) : (
        <p className="text-[11px] text-slate-500 italic">No saved templates yet. Configure a form below and click "Save Form as Template" to reuse it across your workspace.</p>
      )}
    </div>
  );
}
